<template>
  <ion-modal :is-open="isOpen" :backdrop-dismiss="false" class="card-modal">
    <ion-card>
      <ion-card-header>
        <ion-card-title>
          Enemies Defeated{{ levelWinData ? ` in ${levelWinData.currentMoveCount} Moves` : '' }}{{ levelWinData && levelWinData.currentUndoCount > 0 ? ` (+${levelWinData.currentUndoCount})` : '' }}!
        </ion-card-title>
      </ion-card-header>
      <ion-card-content>
        <p>Hexwood Forest is a little safer today, because of you.</p>

        <!-- Perfect solution message -->
        <div v-if="levelWinData && isPerfectSolution" class="perfection-message">
          <h3>Perfection! ⭐</h3>
          <p class="perfection-text">Best solution: {{ levelWinData.bestSolutionMoveCount }} Moves</p>
        </div>

        <!-- Normal stats -->
        <div v-else-if="levelWinData" class="move-stats">
          <div class="stat-row">
            <span class="stat-label">Your moves:</span>
            <span class="stat-value">
              {{ levelWinData.currentMoveCount }}
              <span v-if="levelWinData.currentUndoCount > 0" class="undo-badge">
                <span class="undo-count">+{{ levelWinData.currentUndoCount }}</span>
                <ion-icon :icon="arrowUndoOutline" class="undo-icon"></ion-icon>
              </span>
            </span>
          </div>

          <div v-if="levelWinData.bestPlayerMoveCount !== undefined" class="stat-row">
            <span class="stat-label">Your best:</span>
            <span class="stat-value" :class="{ 'new-record': isNewRecord }">
              {{ levelWinData.bestPlayerMoveCount }}
              <span v-if="isNewRecord" class="record-badge">New Record!</span>
            </span>
          </div>

          <div v-if="levelWinData.bestSolutionMoveCount !== null" class="stat-row">
            <span class="stat-label">Best solution:</span>
            <span class="stat-value">{{ levelWinData.bestSolutionMoveCount }}</span>
          </div>
        </div>

        <ion-button v-if="pendingReward" @click="$emit('claim-reward')" expand="block" color="warning">Claim Reward</ion-button>
        <ion-button v-else-if="hasNextLevel" @click="$emit('continue')" expand="block">Next Level</ion-button>
        <ion-button v-else @click="$emit('continue')" expand="block">Continue</ion-button>
        <ion-button @click="$emit('replay')" expand="block" fill="outline">Play this level again</ion-button>
        <ion-button
          v-if="hasPlayerSolution"
          @click="$emit('replaySolution')"
          expand="block"
          fill="outline"
        >
          <ion-icon :icon="playCircleOutline" slot="start"></ion-icon>
          Replay Your Solution
        </ion-button>
        <ion-button @click="$emit('home')" expand="block" color="light">Return to Home</ion-button>
      </ion-card-content>
    </ion-card>
  </ion-modal>
</template>

<script setup>
import { inject, computed } from 'vue';
import { IonModal, IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon } from '@ionic/vue';
import { arrowUndoOutline, playCircleOutline } from 'ionicons/icons';

defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
});

defineEmits(['continue', 'claim-reward', 'replay', 'replaySolution', 'home']);

const game = inject('game');
const levelWinData = computed(() => game.state.gameplay.levelWinData);
const hasPlayerSolution = computed(() => {
  return levelWinData.value?.currentMoveCount > 0;
});
const isNewRecord = computed(() => {
  if (!levelWinData.value) return false;
  const { currentMoveCount, bestPlayerMoveCount } = levelWinData.value;
  return currentMoveCount === bestPlayerMoveCount;
});
const isPerfectSolution = computed(() => {
  if (!levelWinData.value) return false;
  const { currentMoveCount, currentUndoCount, bestSolutionMoveCount } = levelWinData.value;
  return bestSolutionMoveCount !== null && currentMoveCount === bestSolutionMoveCount && currentUndoCount === 0;
});
const pendingReward = computed(() => game.state.progress.completedChapterReward);
const hasNextLevel = computed(() => !game.state.progress.gameComplete);
</script>

<style scoped>
ion-card {
  --background: var(--hw-bg-surface);
  --color: var(--hw-text);
  text-align: center;
  padding: 20px;
}

ion-card-title {
  font-size: 2em;
  font-weight: bold;
  margin-bottom: 20px;
}

.move-stats {
  margin: 20px 0;
  padding: 15px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: var(--hw-radius);
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 1.1em;
}

.stat-label {
  color: var(--hw-text-muted);
}

.stat-value {
  font-weight: bold;
  color: var(--hw-text);
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-value.new-record {
  color: var(--hw-gold);
}

.record-badge {
  margin-left: 8px;
  font-size: 0.8em;
  color: var(--hw-gold);
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.perfection-message {
  margin: 20px 0;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.25);
  border-radius: var(--hw-radius);
  color: var(--hw-gold);
}

.perfection-message h3 {
  font-size: 1.8em;
  font-weight: bold;
  margin: 0 0 10px 0;
  animation: pulse 1.5s ease-in-out infinite;
}

.perfection-text {
  font-size: 1.2em;
  margin: 0;
  color: var(--hw-gold);
}

.undo-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.85em;
  opacity: 0.8;
}

.undo-count {
  font-family: monospace;
}

.undo-icon {
  font-size: 16px;
  vertical-align: middle;
}
</style>
